import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Measurable } from "../../types/Scenario";
import { Dash } from "../../types/Dash";
import useActions from "../../app/hooks";

interface EditMeasurableProps {
  open: boolean;
  measurable: Measurable | null;
  scenarioId: number;
  measurables: Measurable[];
  dashId?: number | null;
  scenarioFolderId?: number | null;
  onClose: () => void;
  onSave: (updated: {
    description: string;
    x: number;
    y: number;
    deps: number[];
    priority: number;
    user_guessed_probability: number;
    for_whom: string;
  }) => void;
}

// Normalize deps from API union shape to number[]
function normalizeDeps(m: Measurable | null): number[] {
  if (!m || m.deps == null) return [];
  const raw = m.deps;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" && Array.isArray((raw as { deps: number[] }).deps)) {
    return (raw as { deps: number[] }).deps;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as { deps?: number[] } | number[];
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray((parsed as { deps?: number[] }).deps)) {
        return (parsed as { deps?: number[] }).deps ?? [];
      }
    } catch {
      return [];
    }
  }
  return [];
}

/** Parse for_whom string (JSON array of entities) to string[] */
function normalizeForWhom(m: Measurable | null): string[] {
  const raw = m?.for_whom;
  if (raw == null || raw === "") return [];
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((e: unknown): e is string => typeof e === "string");
    return [];
  } catch {
    return [];
  }
}

export default function EditMeasurable(props: EditMeasurableProps) {
  const { open, measurable, scenarioId, measurables, dashId, scenarioFolderId, onClose, onSave } =
    props;
  const actions = useActions();

  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [xError, setXError] = useState("");
  const [yError, setYError] = useState("");
  const [priority, setPriority] = useState<number>(0);
  const [userGuessedProbability, setUserGuessedProbability] = useState<number>(0);
  const [forWhom, setForWhom] = useState("");

  const [deps, setDeps] = useState<number[]>([]);
  const [selectedDepId, setSelectedDepId] = useState<number | "">("");

  // Initialize form state when dialog opens or measurable changes.
  useEffect(() => {
    if (!open || !measurable) return;
    setDescription(measurable.description);
    setDescriptionError("");
    setX(measurable.x);
    setY(measurable.y);
    setXError("");
    setYError("");
    setPriority(measurable.priority ?? 0);
    setDeps(normalizeDeps(measurable));
    setSelectedDepId("");
    setUserGuessedProbability(measurable.user_guessed_probability ?? 0);
    setForWhom(normalizeForWhom(measurable).join(", "));
  }, [open, measurable]);

  const availableDeps = useMemo(
    () =>
      measurables.filter(
        (m) => m.id != null && m.id !== measurable?.id && !deps.includes(m.id!)
      ),
    [measurables, measurable, deps]
  );

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setDescription(v);
    setDescriptionError(v.trim() === "" ? "Description is required" : "");
  };

  const handleSave = () => {
    if (!measurable || measurable.id == null) return;
    let ok = true;
    if (description.trim() === "") {
      setDescriptionError("Description is required");
      ok = false;
    }
    const xNum = Number(x);
    const yNum = Number(y);
    if (Number.isNaN(xNum)) {
      setXError("Invalid number");
      ok = false;
    } else {
      setXError("");
    }
    if (Number.isNaN(yNum)) {
      setYError("Invalid number");
      ok = false;
    } else {
      setYError("");
    }
    if (!ok) return;

    const entities = forWhom
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    onSave({
      description,
      x: xNum,
      y: yNum,
      deps,
      priority,
      user_guessed_probability: userGuessedProbability,
      for_whom: JSON.stringify(entities),
    });
  };

  const handleAddDep = () => {
    if (selectedDepId === "") return;
    const id = selectedDepId as number;
    if (!deps.includes(id)) {
      setDeps((prev) => [...prev, id]);
    }
    setSelectedDepId("");
  };

  const handleRemoveDep = (id: number) => {
    setDeps((prev) => prev.filter((d) => d !== id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit measurable</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Change description, position, priority, and dependencies.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Description"
          fullWidth
          variant="standard"
          value={description}
          onChange={handleDescriptionChange}
          error={descriptionError !== ""}
          helperText={descriptionError}
        />
        <TextField
          margin="dense"
          label="X"
          type="number"
          fullWidth
          variant="standard"
          value={x}
          onChange={(e) => setX(Number(e.target.value))}
          error={xError !== ""}
          helperText={xError}
          inputProps={{ step: 1 }}
        />
        <TextField
          margin="dense"
          label="Y"
          type="number"
          fullWidth
          variant="standard"
          value={y}
          onChange={(e) => setY(Number(e.target.value))}
          error={yError !== ""}
          helperText={yError}
          inputProps={{ step: 1 }}
        />
        <TextField
          margin="dense"
          label="Priority"
          type="number"
          fullWidth
          variant="standard"
          value={priority}
          onChange={(e) => setPriority(Number(e.target.value))}
          inputProps={{ step: 1, min: 0 }}
        />
      <TextField
        margin="dense"
        label="User guessed probability (0–100)"
        type="number"
        fullWidth
        variant="standard"
        value={userGuessedProbability}
        onChange={(e) => setUserGuessedProbability(Number(e.target.value))}
        inputProps={{ step: 1, min: 0, max: 100 }}
      />
      <TextField
        margin="dense"
        label="For whom (comma-separated entities)"
        fullWidth
        variant="standard"
        value={forWhom}
        onChange={(e) => setForWhom(e.target.value)}
        placeholder="e.g. Team A, Product X"
      />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Dependencies
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          Select a measurable from the dropdown and click ADD to assign as a dependency.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <FormControl variant="standard" sx={{ minWidth: 200 }}>
            <InputLabel id="edit-measurable-deps-label">Add dependency</InputLabel>
            <Select
              labelId="edit-measurable-deps-label"
              value={selectedDepId}
              onChange={(e) =>
                setSelectedDepId(
                  e.target.value === "" ? "" : (e.target.value as number)
                )
              }
              label="Add dependency"
            >
              <MenuItem value="">None</MenuItem>
              {availableDeps.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.description} (id {m.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddDep}
            disabled={selectedDepId === "" || availableDeps.length === 0}
          >
            ADD
          </Button>
        </Box>
        <Box sx={{ maxHeight: 160, overflow: "auto" }}>
          {deps.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No dependencies assigned.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {deps.map((id) => {
                const dep = measurables.find((m) => m.id === id);
                return (
                  <Box
                    key={id}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: "grey.200",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" sx={{ mr: 0.5 }}>
                      {dep?.description ?? `id ${id}`}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveDep(id)}
                      aria-label="Remove dependency"
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Workbook
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          Create a workbook (dash) for this measurable, or open the assigned one.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {measurable?.dash_id != null ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                actions.setEditingDash({ id: measurable!.dash_id! } as Dash);
                onClose();
              }}
            >
              Open workbook
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (!measurable?.id) return;
                const entities = forWhom
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                actions.createDashAndAssignToMeasurable({
                  scenarioId,
                  measurableId: measurable.id,
                  description,
                  x,
                  y,
                  deps,
                  dashName: description,
                  terms: "",
                  folder_id: scenarioFolderId ?? undefined,
                  user_guessed_probability: userGuessedProbability,
                  for_whom: JSON.stringify(entities),
                });
                onClose();
              }}
            >
              Create workbook for this measurable
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={description.trim() === ""}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

